'use client';

import { SEARCH_DEBOUNCE_MS } from '@util/conversations/conversationUtils';
import type { MainFilters } from '@util/firestore/messages';
import { MAIN_FILTERS } from '@util/firestore/messages';
import useDebounce from '@util/hooks/useDebounce';
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';

interface ConversationsContextType {
  availableFilters: {
    label: string;
    value: MainFilters;
  }[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  debouncedSearchQuery: string;
  activeFilter: MainFilters;
  setActiveFilter: (filter: MainFilters) => void;
}

const ConversationsContext = createContext<ConversationsContextType>(
  {} as ConversationsContextType
);

export const useConversations = () => {
  const context = useContext(ConversationsContext);
  if (!context) {
    throw new Error(
      'useConversations must be used within ConversationsProvider'
    );
  }
  return context;
};

export function ConversationsProvider({ children }: { children: ReactNode }) {
  const { userDoc } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);
  const [activeFilter, setActiveFilter] = useState<MainFilters>('all');
  const availableFilters = useMemo(() => {
    return Object.keys(MAIN_FILTERS)
      .filter((key) => {
        if (key === 'offer_states') return false;
        if (key.startsWith('support_')) return false;
        if (key.startsWith('source_')) return false;
        if (
          (key === 'flagged' || key === 'agent' || key === 'source') &&
          !userDoc?.roles?.admin &&
          !userDoc?.roles?.support
        )
          return false;
        return true;
      })
      .map((key) => ({
        label: MAIN_FILTERS[key as keyof typeof MAIN_FILTERS] as string,
        value: key as MainFilters,
      }));
  }, [userDoc?.roles]);

  return (
    <ConversationsContext.Provider
      value={{
        availableFilters,
        searchQuery,
        setSearchQuery,
        activeFilter,
        setActiveFilter,
        debouncedSearchQuery,
      }}
    >
      {children}
    </ConversationsContext.Provider>
  );
}
